import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  Inject,
  Injector,
  Input,
  LOCALE_ID,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { OrderformFascade } from '../../../../fascades/orderform.fascade';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { map } from 'rxjs/operators';
import { BehaviorSubject, firstValueFrom, fromEvent, merge, of, Subject, Subscription } from 'rxjs';
import { AbstractMethodComponent } from '../abstract-method.component';
import {
  OrderformOrderState,
  OrderformRedirectResponse,
  OrderformSettingsState,
  OrderformState,
  OrderFormValidationResult,
} from '@orderform/widgets';
import { CREDIT_CARD_DEFAULT_STYLES, CreditCardStyles } from '../../payment-methods.model';
import { HttpClient } from '@angular/common/http';
import { ExternalResourcesLoaderService } from '../../../../services/external-resources-loader.service';
import {
  EventTypes,
  ICardPaymentMethodManager,
  Payment,
  Primer,
  PrimerHeadlessCheckout,
} from '@primer-io/checkout-web';
import { CvcDialogComponent } from '../../cvc-dialog/cvc-dialog.component';
import { CreditCardField, PrimerInputArgs, PrimerStatusReason } from './primer.model';
import { CustomErrorMessageService } from '../../services/custom-error-messages.service';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { WaitingService } from 'libs/orderform-widgets/src/lib/services/waiting.service';

@Component({
  selector: 'orderform-payment-method-primer',
  templateUrl: './primer.component.html',
  styleUrls: ['./primer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PrimerComponent),
      multi: true,
    },
  ],
})
export class PrimerComponent
  extends AbstractMethodComponent
  implements OnInit, OnDestroy, ControlValueAccessor, AfterViewInit
{
  @Input() config: {
    payment_provider_id: number;
    jsSdkUrls: string[];
    sdkType: 'headless' | 'dropin';
    locale: string;
    has3dSecure: boolean;
    debugLabel: string;
    method: 'creditcard';
    config: any;
    clientIp: string;
  };
  @Input() styles?: CreditCardStyles = CREDIT_CARD_DEFAULT_STYLES;
  @ViewChild('cvcDialog') cvcDialog!: CvcDialogComponent;
  @ViewChild('cardNumberDiv') cardNumberDiv!: ElementRef;
  @ViewChild('cardExpiryDiv') cardExpiryDiv!: ElementRef;
  @ViewChild('cardCvvDiv') cardCvvDiv!: ElementRef;

  protected readonly _subscriptionBeforeSubmit = this._orderformFascade.beforeSubmit.subscribe(() =>
    this._onBeforeSubmit()
  );
  protected readonly _subscriptionFormValidation = this._orderformFascade.formValidation.subscribe(
    () => this._onFormValidation(),
    this._element
  );
  protected _settings: OrderformSettingsState;
  protected readonly _settings$ = this._orderformFascade.settings$.subscribe((settings) => {
    this._settings = settings;
  });

  protected _headless: PrimerHeadlessCheckout;
  protected _cardManager: ICardPaymentMethodManager;

  public creditCardType$ = new Subject<string>();

  protected clientToken: string;

  public successByField$ = {
    cardNumber: new BehaviorSubject<boolean>(false),
    expirationDate: new BehaviorSubject<boolean>(false),
    cvv: new BehaviorSubject<boolean>(false),
  };
  public errorByField$ = {
    cardNumber: new BehaviorSubject<string>(''),
    expirationDate: new BehaviorSubject<string>(''),
    cvv: new BehaviorSubject<string>(''),
  };
  public touchedByField$ = {
    cardNumber: new BehaviorSubject<boolean>(false),
    expirationDate: new BehaviorSubject<boolean>(false),
    cvv: new BehaviorSubject<boolean>(false),
  };

  protected readonly _subscriptionPrimerRedirect = this._orderformFascade.primerRedirectIntercepted$.subscribe(
    ({ redirectData }) => {
      this._handlePrimerRedirect(redirectData);
    }
  );

  protected _redirectUrl: string;
  protected _numberOfSubmitCalls = 0;
  protected _renewSwitchIntervalAuthKeyId = '';
  protected _detectedCardNetworks = [];
  protected _showNetworkToggle$ = new BehaviorSubject<boolean>(false);
  protected _selectedNetwork: string | null = null;

  constructor(
    protected readonly _orderformFascade: OrderformFascade,
    @Inject(LOCALE_ID) private readonly _locale: string,
    injector: Injector,
    private readonly http: HttpClient,
    public externalResourcesLoader: ExternalResourcesLoaderService,
    private _ngZone: NgZone,
    protected _customErrorMessageService: CustomErrorMessageService,
    protected _store: Store<OrderformState>,
    protected _dialog: MatDialog,
    private _waitingService: WaitingService
  ) {
    super(injector);
  }

  networkStatus: boolean = false;
  networkStatus$: Subscription = Subscription.EMPTY;

  ngOnInit(): void {
    this.checkNetworkStatus();
  }

  checkNetworkStatus() {
    this.networkStatus = navigator.onLine;
    this.networkStatus$ = merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline'))
      .pipe(map(() => navigator.onLine))
      .subscribe((status) => {
        this.networkStatus = status;
      });
  }

  public async ngAfterViewInit() {
    await this.initializePrimerHeadless();
  }

  async initializePrimerHeadless() {
    try {
      const order = await this.getOrder();
      const clientSession = await this.createClientSession(order);
      this.clientToken = clientSession.clientToken;

      if (!clientSession || !this.clientToken) {
        this._toaster.error(
          $localize`:@@payment_method.error.not_available:Der Bezahlanbieter steht aktuell nicht zur Verfügung. Bitte probieren es mit einer anderen Bezahlweise nochmal.`
        );
        return;
      }

      this._headless = await Primer.createHeadless(this.clientToken, {
        onAvailablePaymentMethodsLoad: async (paymentMethods) => {
          // Called when the available payment methods are retrieved
          for (const paymentMethod of paymentMethods) {
            const { managerType } = paymentMethod;
            switch (managerType) {
              case 'CARD': {
                this._cardManager = await this._headless.createPaymentMethodManager('PAYMENT_CARD', {
                  onCardNetworksChange: (data) => {
                    this._ngZone.run(() => {
                      this._detectedCardNetworks = data.detectedCardNetworks.items;

                      // Show toggle only when multiple networks are detected
                      this._showNetworkToggle$.next(
                        this._detectedCardNetworks.length > 1 && this.successByField$.cardNumber.value
                      );

                      if (this._detectedCardNetworks.length === 1) {
                        const network = this._detectedCardNetworks[0].network;
                        this._selectedNetwork = network;
                        this.creditCardType$.next(
                          network.toLowerCase() === 'amex' ? 'americanexpress' : network.toLowerCase()
                        );
                      } else if (this._detectedCardNetworks.length > 1) {
                        // If we haven't selected a network yet, default to the first one
                        if (!this._selectedNetwork) {
                          this._selectedNetwork = this._detectedCardNetworks[0].network;
                          this.creditCardType$.next(
                            this._selectedNetwork.toLowerCase() === 'amex'
                              ? 'americanexpress'
                              : this._selectedNetwork.toLowerCase()
                          );
                        }
                      } else {
                        this._selectedNetwork = null;
                        this.creditCardType$.next('');
                      }
                    });
                  },
                });

                const { cardNumberInput, expiryInput, cvvInput } = this._cardManager.createHostedInputs();
                const style = {
                  input: {
                    base: {
                      placeholder: {
                        color: this.styles.placeholderColor,
                        fontSize: parseFloat(this.styles.fontSize) * 16 + 'px',
                      },
                      selection: {
                        fontSize: parseFloat(this.styles.fontSize) * 16 + 'px',
                        color: this.styles.textColor,
                      },
                      fontSize: parseFloat(this.styles.fontSize) * 16 + 'px',
                      height: '100%',
                    },
                  },
                };

                cardNumberInput.addEventListener(EventTypes.CHANGE, (...args: any) => {
                  this.validationHandler('cardNumber', ...args);
                });

                expiryInput.addEventListener(EventTypes.CHANGE, (...args: any) => {
                  this.validationHandler('expirationDate', ...args);
                });

                cvvInput.addEventListener(EventTypes.CHANGE, (...args: any) => {
                  this.validationHandler('cvv', ...args);
                });

                await Promise.all([
                  cardNumberInput.render(this.cardNumberDiv.nativeElement, {
                    placeholder: '4444 3333 2222 1111',
                    ariaLabel: 'Card number',
                    style,
                  }),
                  expiryInput.render(this.cardExpiryDiv.nativeElement, {
                    placeholder: '03 / ' + ('' + (new Date().getFullYear() + 2)).slice(-2),
                    ariaLabel: 'Expiry date',
                    style,
                  }),
                  cvvInput.render(this.cardCvvDiv.nativeElement, {
                    placeholder: '•••',
                    ariaLabel: 'CVV',
                    style,
                  }),
                ]);
                break;
              }
            }
          }
        },
        onCheckoutComplete: async (data) => {
          await this._handleCheckoutComplete(data);
        },
        onCheckoutFail: async (error, { payment }, handler) => {
          return this._handleCheckoutFail(error, payment, handler, 'ds-orderform-payment-stripe-v2');
        },
        card: {
          cardholderName: {
            required: false,
          },
        },
      });

      await this._headless.start();
    } catch (error) {
      this._hidePaymentMethodWithError(error, 'primer');
    }
  }

  validationHandler(field: CreditCardField, ...args: PrimerInputArgs[]) {
    if (args[0].dirty) {
      this.touchedByField$[field].next(true);
      this.successByField$[field].next(args[0].valid);

      if (field === 'cardNumber' && this._detectedCardNetworks.length > 1) {
        this._showNetworkToggle$.next(args[0].valid);
      }

      if (!args[0].valid) {
        this.errorByField$[field].next($localize`:@@payment_method.error.invalid_input:Ungültige Eingabe.`);
      } else {
        this.errorByField$[field].next('');
      }
    }
  }

  onCvvHelpClick(event: Event) {
    event.preventDefault();
    this.cvcDialog.open();
  }

  async getOrder() {
    return firstValueFrom(this._orderformFascade.order$.pipe(map((order) => order)));
  }

  createClientSession(order: OrderformOrderState): Promise<{ clientToken: string; id: string }> {
    const { protocol, hostname, port, href } = window.location;
    const baseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    const apiUrl = baseUrl + '/buy/primer/create_client_session';

    const postData = {
      currency: order.order.currency,
      amount: order.order_summary?.order_final?.gross_1st ?? 0,
      email: order.order.shipping_address?.buyer_address_email ?? '',
      payment_plan_id: order.order.payment_plan_id,
      payment_provider_id: order.order.payment_provider.payment_provider_id,
      customer_id: order.order.shipping_address?.buyer_address_email || '',
      country:
        order.order.shipping_address?.buyer_address_country ||
        order.order.shipping_address?.buyer_address_private_country ||
        '',
      url: href,
    };

    try {
      return firstValueFrom(this.http.post<{ clientToken: string; id: string }>(apiUrl, postData));
    } catch (error) {
      console.error('Error creating client session:', error);
      throw error;
    }
  }

  protected async _onBeforeSubmit(): Promise<{ valid: boolean }> {
    if (!this.isActive()) {
      return Promise.resolve({ valid: true });
    }

    const { valid } = await this._cardManager.validate();
    const refreshed = await this._headless.refreshClientSession();

    if (!valid || !refreshed || !this.clientToken) {
      this._paymentDataChanged.next({
        valid: false,
      });
      return { valid: false };
    }

    const isConfirmFlow = window.location.pathname.includes('/confirm/');

    const payload: any = {
      valid: true,
      primer_token: this.clientToken,
    };

    // Signal BE to keep current upsell step for multi-submit Primer flow
    if (isConfirmFlow) {
      payload.primer_double_submit = true;
    }

    this._paymentDataChanged.next(payload);

    return { valid: true };
  }

  protected _onFormValidation(): Promise<OrderFormValidationResult> {
    if (!this.isActive()) {
      return Promise.resolve({
        valid: true,
        component: this.getComponentName(),
        invalidFieldList: [],
      });
    }

    const invalidFieldList = this._getInvalidFields();

    return Promise.resolve({
      valid: invalidFieldList.length === 0,
      component: this.getComponentName(),
      invalidFieldList,
    });
  }

  protected _getInvalidFields(): Array<{ id: string; errors: string[] }> {
    return Object.keys(this.errorByField$)
      .filter((field) => this.errorByField$[field].value)
      .map((field) => ({
        id: field,
        errors: [this.errorByField$[field].value],
      }));
  }

  protected async _handlePrimerRedirect(redirectData: OrderformRedirectResponse): Promise<void> {
    const shouldContinue = await this._handlePrimerRedirectCommon(redirectData);
    if (!shouldContinue) return;

    const { valid } = await this._cardManager.validate();
    const refreshed = await this._headless.refreshClientSession();

    if (valid && refreshed && this.clientToken) {
      // Submit the card input data to Primer for tokenization

      try {
        if (this._detectedCardNetworks.length > 1 && this._selectedNetwork) {
          await this._cardManager.submit({ cardNetwork: this._selectedNetwork.toUpperCase() });
        } else {
          await this._cardManager.submit();
        }
        this._numberOfSubmitCalls++;
        this._redirectUrl = redirectData.url;
      } catch (error) {
        const orderData = await this.getOrder();
        if (
          !this.networkStatus ||
          error.name === 'NetworkError' ||
          (error instanceof TypeError && error.message.includes('network')) ||
          error.message.includes('internet')
        ) {
          this.handleNetworkError();
        }
        if (error?.details?.invalidFieldKeys?.length > 0) {
          error?.details?.invalidFieldKeys.forEach((fieldKey) => {
            this.successByField$[fieldKey].next(false);
            this.errorByField$[fieldKey].next($localize`:@@payment_method.error.invalid_input:Ungültige Eingabe.`);
            this._monitoring.trackValidationError(fieldKey + ':invalid', 'frontend', 'ds-orderform-payment-primer');

            const labels = this._monitoring.getLabelsFromFailedTransaction(
              orderData,
              error,
              'ds-orderform-payment-primer'
            );
            this._monitoring.addLabels(labels);
          });
        } else {
          this._toaster.error(error.message);
          this._monitoring.trackValidationError(
            'exception:' + error.message,
            'frontend',
            'ds-orderform-payment-primer'
          );

          const labels = this._monitoring.getLabelsFromFailedTransaction(
            orderData,
            error,
            'ds-orderform-payment-primer'
          );
          this._monitoring.addLabels(labels);
        }
      }
    }
  }

  protected async _handlePrimerRedirectCommon(redirectData: OrderformRedirectResponse): Promise<boolean> {
    //Special case for Primer redirects because they are handled differently
    //We are doing 2 submit calls to handle the redirect and payment status properly
    if (this._numberOfSubmitCalls >= 1) {
      window.location.href = redirectData.url;
      return false; // Signal that we've redirected
    }

    const isSwitchIntervalAddPackageRenewFlow =
      window.location.href.includes('/switch_interval/') || window.location.href.includes('/add_package/');

    // If we are in the switch interval renew flow, we need to extract the purchase_id from the URL
    // and set it in the order store, so that the renew flow can handle it properly
    // We also save the auth key ID from the URL for the second submit call to our BE
    if (isSwitchIntervalAddPackageRenewFlow) {
      const urlObj = new URL(redirectData.url);
      const pathSegments = urlObj.pathname.split('/').filter((segment) => segment);

      // URL format is expected to be like: /receipt/22/SCUP3U33A/CDFU4ERW/0/renew
      if (pathSegments.length >= 5) {
        const purchaseId = pathSegments[2];
        this._renewSwitchIntervalAuthKeyId = pathSegments[3];

        const order = await this.getOrder();
        this._orderformFascade.setOrderStore({
          ...order,
          purchase_id: purchaseId,
        });
      }
    }

    return true; // Signal that we should continue processing
  }

  protected async _handleCheckoutComplete(data: { payment: Payment }): Promise<void> {
    const order = await this.getOrder();
    this._orderformFascade.setOrderStore({
      ...order,
      purchase_id: data.payment.orderId,
      execute: {
        action: 'submit',
      },
      payment_provider_meta: {
        primer_token: this.clientToken,
        primer_pay_id: data.payment.id,
        new_order_id: data.payment.orderId,
      },
    });

    const isSwitchIntervalAddPackageRenewFlow =
      window.location.href.includes('/switch_interval/') || window.location.href.includes('/add_package/');

    // This is a special case for the Primer payment method
    // If we are in the switch interval or add package renew flow, we need to submit to the 'pay' endpoint in the Renew controller
    // Otherwise, we do regular submit
    if (isSwitchIntervalAddPackageRenewFlow) {
      await firstValueFrom(this._orderformFascade.submitRenewPrimerOrderForm(this._renewSwitchIntervalAuthKeyId));
    } else {
      await firstValueFrom(this._orderformFascade.submitOrderForm());
    }
  }

  protected async _handleCheckoutFail(
    error: any,
    payment: any,
    handler: any,
    componentName: string = 'ds-orderform-payment-primer'
  ): Promise<void> {
    if (!handler) {
      return;
    }

    const status = await this.getDeclineStatus(payment.id);
    const declinedErrorMessage = this._customErrorMessageService.getCustomErrorMessageFromPrimer(
      status && status.statusReason ? status.statusReason.code : 'DECLINED'
    );

    this._toaster.error(declinedErrorMessage);
    this._monitoring.trackValidationError('Exception: Primer payment failed', 'frontend', componentName);

    this._waitingService.hide();

    return handler.showErrorMessage();
  }

  protected getComponentName(): string {
    return 'ds-orderform-payment-primer';
  }

  async getDeclineStatus(id: string): Promise<{ statusReason: PrimerStatusReason }> {
    const { protocol, hostname, port, href } = window.location;
    const baseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    const apiUrl = baseUrl + '/buy/primer/get_payment_decline_status';
    const order = await this.getOrder();

    const postData = {
      payment_provider_id: order.order.payment_provider.payment_provider_id,
      payment_id: id,
    };

    try {
      return firstValueFrom(this.http.post<{ statusReason: PrimerStatusReason }>(apiUrl, postData));
    } catch (error) {
      console.error('Error fetching the decline status:', error);
      throw error;
    }
  }

  public selectNetwork(network: string): void {
    this._selectedNetwork = network;
    this.creditCardType$.next(this._selectedNetwork.toLowerCase());
  }

  public getNetworkClass(network: string): string {
    const normalizedNetwork = network.toLowerCase();

    // Map network names to their CSS classes
    const networkMap: { [key: string]: string } = {
      visa: 'visa-icon',
      mastercard: 'mastercard-icon',
      amex: 'americanexpress-icon',
      american_express: 'americanexpress-icon',
      discover: 'discover-icon',
      diners: 'diners-club-icon',
      diners_club: 'diners-club-icon',
      jcb: 'jcb-icon',
      cartes_bancaires: 'cartes-bancaires-icon',
      bancontact: 'bancontact-icon',
      dankort: 'dankort-icon',
    };

    return networkMap[normalizedNetwork] || 'placeholder-icon';
  }

  private handleNetworkError(): void {
    this._ngZone.run(() => {
      this._toaster.error(
        $localize`:@@payment_method.error.interrupted:Es sieht so aus, als wäre Ihre Transaktion unterbrochen worden und wir konnten die Zahlung nicht abschließen. Bitte versuchen Sie es erneut.`
      );
    });
  }

  ngOnDestroy() {
    this._subscriptionBeforeSubmit.unsubscribe();
    this._subscriptionFormValidation.unsubscribe();
    this._settings$.unsubscribe();
    this._subscriptionPrimerRedirect.unsubscribe();
  }
}
